import api from "config/api";
import { useAppContext } from "contexts/AppContext";
import { useCallback, useEffect, useMemo, useReducer } from "react";
import { getEnvVar } from "utils/environment";
import useAuthToken from "./useAuthToken";
import useMutableRef from "./useMutableRef";
import { decryptWithAES } from 'converter/converter'

const requestReducer = (initialData) => (_, action) => {
  switch (action.type) {
    case "success":
      return { data: action.data, loading: false };
    case "loading": {
      return { data: initialData, loading: true };
    }
    case "error":
      return { data: initialData, loading: false, error: action.error };
    default:
      return { data: initialData, loading: false };
  }
};

const useAxiosRequest = ({
  initialData,
  autoRun,
  globalError = true,
  ...config
} = {}) => {
  const { setGlobalError } = useAppContext();
  const getAuthToken = useAuthToken();

  const [{ data, loading, error }, dispatch] = useReducer(
    requestReducer(initialData),
    { data: initialData, loading: autoRun }
  );

  const decodedClientId = decryptWithAES(getEnvVar("CLIENT_ID"))
  const loadingRef = useMutableRef(loading);
  const configRef = useMutableRef(config);

  const run = useCallback(
    (innerConfig = configRef.current) => {
      const _run = async () => {
        try {
          const token = await getAuthToken();
          const { headers } = innerConfig;
          const result = await api.request({
            ...innerConfig,
            headers: {
              "Content-type": "application/json",
              Authorization: `Bearer ${token}`,
              "x-ibm-client-id": decodedClientId,
              ...headers,
            },
          });
          dispatch({ type: "success", data: result?.data });
          return { type: "success", data: result?.data };
        } catch (err) {
          dispatch({ type: "error", error: err });
          if (globalError) {
            setGlobalError(err);
          }
          return { type: "error", error: err };
        }
      };

      if (!loadingRef.current) {
        dispatch({ type: "loading" });
      }
      return _run();
    },
    [configRef, loadingRef, getAuthToken, globalError, setGlobalError]
  );

  useEffect(() => {
    if (autoRun) {
      run();
    }
  }, [autoRun, run]);

  return useMemo(
    () => ({
      data,
      loading: loading,
      error: error,
      run,
    }),
    [data, error, loading, run]
  );
};

export default useAxiosRequest;
